import styled from 'styled-components'
import { FlatenedRentChargeData } from '@/custom-types'
import { GetContractQuery, useDeleteRentChargeFromQueryMutation } from '@/graphql/generated'
import { ButtonWithDropDownMenu } from '../molecules'
import textsData from '@/textData/ContractDetailTexts.json'
import { normalizeDate } from '@/helpers/normalizeDate'
import { rentChargeStatusMapper, rentChargeTypeMapper } from '@/helpers'
import { AddRentChargeFormModal } from '../templates'
import { useAddRentChargeFormModalContext } from '@/customHooks/useAddRentChargeFormModalContext'
import { Col, Row } from 'react-grid-system'
import { Button, LoadingSpinner, RentChargeStatusOptionPill } from '../atoms'
import {
  useContractDetailsContext,
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  useUserLanguage,
} from '@/customHooks'

type RentChargeProps = NonNullable<GetContractQuery['contract']>['rentCharges'][number]

const StyledGridTable = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg} ;
  padding-bottom: ${({ theme }) => theme.spacing.lg} ;
  border-radius: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 64px;
`;

const StyledGridHeader = styled.div`
  display: contents;
  & > div {
    font-weight: bold;
    text-align: left;
    padding: 15px;
    border-top: 1px solid grey;
    border-bottom: 1px solid gray;
  }
`;

const StyledGridRow = styled.div`
  display: contents;
  
  & > div {
    padding: 15px;
    background-color: ${({ theme }) => theme.colors.light};
    font-size: 14px;
    padding-top: 1rem;
    padding-bottom: 1rem;
    align-self: stretch;
    place-content: center;
    border-bottom: 1px solid lightgray;
  }

  &:nth-of-type(odd) > div {
    background-color: ${({ theme }) => theme.colors.background2};
  }
`;

export const ContractDetailRentChargesList = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.rentCharges : textsData.en.rentCharges

  const { contract, setContract } = useContractDetailsContext()
  const rentCharges = contract?.rentCharges

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { openModal } = useAddRentChargeFormModalContext()

  const { open: errorHandle } = useErrorHandlerContext()

  const [deleteRentCharge, {loading, error}] = useDeleteRentChargeFromQueryMutation()

  const dataFlatter = (data: RentChargeProps[] | undefined): (FlatenedRentChargeData & { rawStatus: RentChargeProps['rentChargeStatusOption'] })[]  => {
    if (!data || data.length === 0) return []

    return data.map((rentCharge) => ({
      id: rentCharge.id,
      amountValue: '€' + rentCharge.amountValue,
      rentChargeType: rentChargeTypeMapper(rentCharge.rentChargeTypeOption, userLanguage),
      lateCharge: '€0.00',
      rentTerm: normalizeDate(rentCharge.executionDate),
      status: rentChargeStatusMapper(rentCharge.rentChargeStatusOption, userLanguage),
      rawStatus: rentCharge.rentChargeStatusOption,
    }))
  }
  
  const onEditHandle = (id: string) => {
    const rentCharge = rentCharges?.find((rentCharge) => rentCharge.id === id)
    
    if (!rentCharge) return

    openModal(rentCharge)
  }

  const onDeleteHandle = (rentChargeId: string) => {
    deleteRentCharge({ variables: {
      contractId: contract?.id ?? '',
      rentChargeId
    }})
    .then((response) => {
      confirmDialogModalOpen({
        title: texts.deleteConfirmation.title,
        description: `${texts.deleteConfirmation.description}. description: ${response.data?.deleteRentChargeFromContract.description}`
      })
      setContract(undefined)
    })
    .catch(errorHandle)
  }

  const tableData = dataFlatter(rentCharges)

  if (error) errorHandle(error)

  if (loading) return (
    <Row justify="center">
      <Col xs='content'>
        <LoadingSpinner />
      </Col>
    </Row>
  )

  return (
    <>
      <Row justify="end" align="center">
        <Col xs='content'>
          <Button
            onClick={() => openModal()}
          >
            {texts.addRentChargeButton}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <StyledGridTable>
            {/* Encabezado */}
            <StyledGridHeader>
              <div>{texts.tableHeaderLabels.label1}</div>
              <div>{texts.tableHeaderLabels.label2}</div>
              <div>{texts.tableHeaderLabels.label3}</div>
              <div>{texts.tableHeaderLabels.label4}</div>
              <div>{texts.tableHeaderLabels.label5}</div>
              <div></div>
            </StyledGridHeader>

            {/* Filas dinámicas */}
            {tableData.map((data) => (
              <StyledGridRow key={data.id}>
                <div>{data.rentChargeType}</div>
                <div>{data.rentTerm}</div>
                <div>{data.lateCharge}</div>
                <div><RentChargeStatusOptionPill option={data.rawStatus} /></div>
                <div>{data.amountValue}</div>
                <div>
                  <ButtonWithDropDownMenu
                    options={[
                      {item: texts.dropdownMenu.edit, action: () => onEditHandle(data.id)},
                      {item: texts.dropdownMenu.delete, action: () => onDeleteHandle(data.id)},
                    ]}
                    position='left'
                  />
                </div>
              </StyledGridRow>
            ))}
          </StyledGridTable>
        </Col>
      </Row>
      <AddRentChargeFormModal />
    </>
  );
}
